import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormGroup, FormControl } from "@angular/forms";

interface SubsectionField {
  fieldName: string;
  fieldType: string;
  fieldLabel: string;
  placeholder?: string;
  options?: any[];
  value?: any;
  defaultValue?: any;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  fullWidth?: boolean;
  decimalSupport?: boolean;
  conditional?: {
    dependsOn: string;
    showWhen: any;
  };
}

interface SubsectionConfig {
  subsectionTitle?: string;
  toggleField: {
    fieldName: string;
    fieldLabel: string;
    defaultValue?: boolean;
  };
  fields: SubsectionField[];
}

@Component({
    selector: "app-subsection",
    templateUrl: "./subsection.component.html",
    styleUrls: ["./subsection.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SubsectionComponent implements OnInit, OnChanges {
  @Input() subsection: SubsectionConfig;
  @Input() formGroup!: UntypedFormGroup;
  @Input() disabled: boolean = false;
  @Output() fieldChange = new EventEmitter<{ field: string; value: any }>();

  isEnabled: boolean = false;

  ngOnInit() {
    this.updateEnabledState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["subsection"] || changes["formGroup"] || changes["disabled"]) {
      this.updateEnabledState();
    }
  }

  updateEnabledState() {
    if (this.formGroup && this.subsection.toggleField) {
      const toggleControl = this.formGroup.get(
        this.subsection.toggleField.fieldName
      );
      if (toggleControl) {
        this.isEnabled = toggleControl.value === true;
        if (this.disabled) {
          toggleControl.disable({ emitEvent: false });
        } else {
          toggleControl.enable({ emitEvent: false });
        }
        this.updateFieldsEnabledState();

        // Listen for changes to the toggle field
        toggleControl.valueChanges.subscribe((value) => {
          this.isEnabled = value === true;
          this.updateFieldsEnabledState();
        });
      }
    }
  }

  updateFieldsEnabledState() {
    if (!this.formGroup || !this.subsection.fields) return;

    this.subsection.fields.forEach((field) => {
      const control = this.formGroup.get(field.fieldName);
      if (control) {
        const shouldEnable = this.isEnabled && !this.disabled;
        if (shouldEnable) {
          control.enable();
        } else {
          control.disable({ emitEvent: false });
          if (!this.isEnabled) {
            const currentVal = control.value;

            const isEmpty =
              currentVal === null ||
              currentVal === undefined ||
              (typeof currentVal === "string" && currentVal.trim() === "");

            if (isEmpty) {
              const minVal = this.getMinValue(field);
              control.setValue(minVal, { emitEvent: false });
            }
          }
        }
      }
    });
  }

  getFormControl(fieldName: string) {
    return this.formGroup ? this.formGroup.get(fieldName) : null;
  }

  shouldShowField(field: SubsectionField): boolean {
    if (!field.conditional) return true;

    const dependentControl = this.formGroup.get(field.conditional.dependsOn);
    const dependentValue = dependentControl.value;

    return dependentValue === field.conditional.showWhen;
  }

  onFieldChange(fieldName: string, value: any) {
    if (this.formGroup && this.formGroup.get(fieldName)) {
      this.formGroup.get(fieldName).setValue(value);
    }
    this.fieldChange.emit({ field: fieldName, value });
  }

  onToggleChange(value: boolean) {
    this.isEnabled = value;
    this.updateFieldsEnabledState();
    this.onFieldChange(this.subsection.toggleField.fieldName, value);
  }

  /**
   * Return the fallback/min value for a field when the subsection is disabled.
   * If a explicit `min` is provided, use that; otherwise pick a sensible type-based default.
   */
  private getMinValue(field: SubsectionField): any {
    if (field.min !== undefined && field.min !== null) {
      return field.min;
    }

    switch (field.fieldType) {
      case "number":
        return 0;
      case "toggle":
      case "checkbox":
        return false;
      default:
        return "";
    }
  }
}
